import React, {
	useCallback,
	forwardRef,
	useMemo,
	useState,
	useImperativeHandle,
} from 'react';
import type {ProviderSearchResult} from '@deezer/next/services/api/Starmania/requests/search/types';
import type {Input} from '@tempo/core';
import {HStack, Skeleton, Text} from '@tempo/core';
import {ContentType} from '@deezer/next/services/types/contentType';
import SearchInput from '@components/SearchInput';
import {useSearch} from '@deezer/next/shared/hooks/useSearch';

type InputProps = React.ComponentProps<typeof Input>;
type ProviderSearchInputProps = {
	selectedProvider?: ProviderSearchResult;
	onSelectedProviderChange: (item?: ProviderSearchResult) => void;
	filterProviderIds?: number[];
} & InputProps;

export type ProviderSearchInputRef = {
	setValue: (value: string) => void;
};

const ProviderSearchInput = forwardRef<
	ProviderSearchInputRef,
	ProviderSearchInputProps
>(
	(
		{selectedProvider, onSelectedProviderChange, filterProviderIds, ...rest},
		ref,
	) => {
		const [value, setValue] = useState(selectedProvider?.name || '');
		const {
			isSearching,
			results,
			error,
			search: searchProvider,
			setResults,
		} = useSearch(ContentType.Provider);

		useImperativeHandle(ref, () => ({
			setValue: (value: string) => setValue(value),
		}));

		const handleChange: React.ChangeEventHandler<
			HTMLInputElement
		> = async e => {
			e.preventDefault();
			const searchValue = e.target.value;
			setValue(searchValue);
			if (selectedProvider) {
				onSelectedProviderChange(undefined);
			}

			if (searchValue) {
				await searchProvider(searchValue);
			} else {
				setResults(undefined);
			}
		};

		const handleResultClick = useCallback(
			(item: ProviderSearchResult) => {
				onSelectedProviderChange(item);
				setValue(item.name);
			},
			[onSelectedProviderChange],
		);

		const handleSearchClear = useCallback(() => {
			setResults(undefined);
			setValue('');
			onSelectedProviderChange(undefined);
		}, [onSelectedProviderChange, setResults]);

		const renderSearchResult = useCallback(
			({name}: any) => (
				<HStack w="100%" top="50px" left="0px" spacing={2}>
					<Text variant="body.default.regular">{name}</Text>
				</HStack>
			),
			[],
		);

		const renderSearchSkeleton = useCallback(
			() => (
				<HStack spacing={2}>
					<Skeleton height={'32px'} width={'32px'} borderRadius={'base'} />
					<Skeleton height={'16px'} width={'100%'} />
				</HStack>
			),
			[],
		);

		const renderSearchPlaceholder = useCallback(
			() => (
				<Text textAlign="center" variant="body.default.regular" py={2} px={4}>
					{/* fixme translate */}
					No provider match this search
				</Text>
			),
			[],
		);

		const renderSearchError = useCallback(
			() => (
				<Text textAlign="center" variant="body.default.regular" py={2} px={4}>
					{/* fixme translate */}
					An error occurred while searching providers. Try again!
				</Text>
			),
			[],
		);

		const filteredResults = useMemo(() => {
			if (!results || !filterProviderIds) {
				return results;
			}

			return results.filter(({id}) => !filterProviderIds.includes(id));
		}, [results, filterProviderIds]);

		return (
			<SearchInput
				value={value}
				error={error}
				onChange={handleChange}
				searchResults={filteredResults}
				isLoading={isSearching}
				renderSearchResult={renderSearchResult}
				renderSearchSkeleton={renderSearchSkeleton}
				renderSearchPlaceholder={renderSearchPlaceholder}
				renderSearchError={renderSearchError}
				searchResultKeyExtractor={({id}) => String(id)}
				onSelectResult={handleResultClick}
				autoComplete="off"
				onSearchClear={handleSearchClear}
				hideInputLeftElement={true}
				{...rest}
			/>
		);
	},
);

ProviderSearchInput.displayName = 'ProviderSearchInput';
export default ProviderSearchInput;
